// Boiler ECM Calculation Engine
// Source of truth: Excel "Boiler Operation - ECM Estimating Tool.xlsm"

export interface BoilerECMInputs {
  // Design
  nameplateInputMmbtuh: number;
  nameplateOutputMmbtuh: number;
  // Operating
  operatingDaysPerYear: number;
  operatingHoursPerDay: number;
  boilerEfficiencyPct: number;
  fuelCostPerTherm: number;
  minConnectedLoadMmbtuh: number;
  maxConnectedLoadMmbtuh: number;
  variableFiringRatePct: number;
  // ECM deltas
  ecm1ReducedHoursPerDay: number;
  ecm2ImprovedEfficiencyPct: number;
  ecm3LoadReductionPct: number;
  ecm4ReducedDaysPerYear: number;
}

export interface BoilerECMCalcResults {
  // Design calcs
  calculatedMaxEfficiencyPct: number;
  calculatedHorsepower: number;
  // Current ops
  runTimeHours: number;
  loadAtSelected: number;
  annualCostAtMin: number;
  annualCostAtMax: number;
  annualCostAtSelected: number;
  hourlyCostAtMin: number;
  hourlyCostAtMax: number;
  hourlyCostAtSelected: number;
  // ECM adjusted params
  ecmOperatingDays: number;
  ecmOperatingHoursPerDay: number;
  ecmEfficiencyPct: number;
  ecmMinLoad: number;
  ecmMaxLoad: number;
  ecmRunTimeHours: number;
  ecmLoadAtSelected: number;
  // ECM costs
  ecmAnnualCostAtMin: number;
  ecmAnnualCostAtMax: number;
  ecmAnnualCostAtSelected: number;
  ecmHourlyCostAtSelected: number;
  // Savings
  savingsAtMin: number;
  savingsAtMax: number;
  savingsMidRange: number;
  savingsPctAtMax: number;
  // Whether any ECM is active
  anyECMActive: boolean;
}

/**
 * Annual cost formula (from Excel):
 * Cost = (load_mmbtuh * 10 * hours/day * days * $/therm) / (efficiency% / 100)
 * Conversion: 1 MMBTUH = 1,000,000 BTU/hr; 1 therm = 100,000 BTU → 10 therms/hr per MMBTUH
 */
function annualCost(
  loadMmbtuh: number,
  hoursPerDay: number,
  daysPerYear: number,
  efficiencyPct: number,
  fuelCostPerTherm: number
): number {
  if (loadMmbtuh <= 0 || efficiencyPct <= 0 || fuelCostPerTherm <= 0) return 0;  // fix B-11
  return (loadMmbtuh * 10 * hoursPerDay * daysPerYear * fuelCostPerTherm) / (efficiencyPct / 100);
}

function hourlyCost(
  loadMmbtuh: number,
  efficiencyPct: number,
  fuelCostPerTherm: number
): number {
  if (loadMmbtuh <= 0 || efficiencyPct <= 0 || fuelCostPerTherm <= 0) return 0;  // fix B-11
  return (loadMmbtuh * 10 * fuelCostPerTherm) / (efficiencyPct / 100);
}

function loadAtFiringRate(minLoad: number, maxLoad: number, firingRatePct: number): number {
  const percentLoad = (maxLoad - minLoad) / 100;
  return (firingRatePct * percentLoad) + minLoad;
}

export function calculateBoilerECM(inputs: BoilerECMInputs): BoilerECMCalcResults {
  const {
    nameplateInputMmbtuh, nameplateOutputMmbtuh,
    operatingDaysPerYear, operatingHoursPerDay,
    boilerEfficiencyPct, fuelCostPerTherm,
    minConnectedLoadMmbtuh, maxConnectedLoadMmbtuh, variableFiringRatePct,
    ecm1ReducedHoursPerDay, ecm2ImprovedEfficiencyPct, ecm3LoadReductionPct, ecm4ReducedDaysPerYear,
  } = inputs;

  // Design calculations
  const calculatedMaxEfficiencyPct = nameplateInputMmbtuh > 0
    ? (nameplateOutputMmbtuh / nameplateInputMmbtuh) * 100
    : 0;
  const calculatedHorsepower = nameplateInputMmbtuh > 0
    ? (nameplateInputMmbtuh * 1_000_000) / 33_417
    : 0;

  // Current operations
  const runTimeHours = operatingDaysPerYear * operatingHoursPerDay;
  const loadAtSelected = loadAtFiringRate(minConnectedLoadMmbtuh, maxConnectedLoadMmbtuh, variableFiringRatePct);

  const annualCostAtMin = annualCost(minConnectedLoadMmbtuh, operatingHoursPerDay, operatingDaysPerYear, boilerEfficiencyPct, fuelCostPerTherm);
  const annualCostAtMax = annualCost(maxConnectedLoadMmbtuh, operatingHoursPerDay, operatingDaysPerYear, boilerEfficiencyPct, fuelCostPerTherm);
  const annualCostAtSelected = annualCost(loadAtSelected, operatingHoursPerDay, operatingDaysPerYear, boilerEfficiencyPct, fuelCostPerTherm);

  const hourlyCostAtMin = hourlyCost(minConnectedLoadMmbtuh, boilerEfficiencyPct, fuelCostPerTherm);
  const hourlyCostAtMax = hourlyCost(maxConnectedLoadMmbtuh, boilerEfficiencyPct, fuelCostPerTherm);
  const hourlyCostAtSelected = hourlyCost(loadAtSelected, boilerEfficiencyPct, fuelCostPerTherm);

  // ECM-adjusted parameters
  const ecmOperatingDays = Math.max(0, operatingDaysPerYear - ecm4ReducedDaysPerYear);
  const ecmOperatingHoursPerDay = Math.max(0, operatingHoursPerDay - ecm1ReducedHoursPerDay);
  const ecmEfficiencyPct = Math.min(100, boilerEfficiencyPct + ecm2ImprovedEfficiencyPct);
  const ecmMinLoad = minConnectedLoadMmbtuh * (1 - ecm3LoadReductionPct / 100);
  const ecmMaxLoad = maxConnectedLoadMmbtuh * (1 - ecm3LoadReductionPct / 100);
  const ecmRunTimeHours = ecmOperatingDays * ecmOperatingHoursPerDay;
  const ecmLoadAtSelected = loadAtFiringRate(ecmMinLoad, ecmMaxLoad, variableFiringRatePct);

  const ecmAnnualCostAtMin = annualCost(ecmMinLoad, ecmOperatingHoursPerDay, ecmOperatingDays, ecmEfficiencyPct, fuelCostPerTherm);
  const ecmAnnualCostAtMax = annualCost(ecmMaxLoad, ecmOperatingHoursPerDay, ecmOperatingDays, ecmEfficiencyPct, fuelCostPerTherm);
  const ecmAnnualCostAtSelected = annualCost(ecmLoadAtSelected, ecmOperatingHoursPerDay, ecmOperatingDays, ecmEfficiencyPct, fuelCostPerTherm);
  const ecmHourlyCostAtSelected = hourlyCost(ecmLoadAtSelected, ecmEfficiencyPct, fuelCostPerTherm);

  // Savings
  const savingsAtMin = annualCostAtMin - ecmAnnualCostAtMin;
  const savingsAtMax = annualCostAtMax - ecmAnnualCostAtMax;
  const savingsMidRange = (savingsAtMin + savingsAtMax) / 2;
  const savingsPctAtMax = annualCostAtMax > 0 ? (savingsAtMax / annualCostAtMax) * 100 : 0;

  const anyECMActive = ecm1ReducedHoursPerDay > 0 || ecm2ImprovedEfficiencyPct > 0 || ecm3LoadReductionPct > 0 || ecm4ReducedDaysPerYear > 0;

  return {
    calculatedMaxEfficiencyPct,
    calculatedHorsepower,
    runTimeHours,
    loadAtSelected,
    annualCostAtMin,
    annualCostAtMax,
    annualCostAtSelected,
    hourlyCostAtMin,
    hourlyCostAtMax,
    hourlyCostAtSelected,
    ecmOperatingDays,
    ecmOperatingHoursPerDay,
    ecmEfficiencyPct,
    ecmMinLoad,
    ecmMaxLoad,
    ecmRunTimeHours,
    ecmLoadAtSelected,
    ecmAnnualCostAtMin,
    ecmAnnualCostAtMax,
    ecmAnnualCostAtSelected,
    ecmHourlyCostAtSelected,
    savingsAtMin,
    savingsAtMax,
    savingsMidRange,
    savingsPctAtMax,
    anyECMActive,
  };
}
